import { LinkOff } from '@mui/icons-material';
import ApiIcon from '@mui/icons-material/Api';
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Step, StepLabel, Stepper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { getGLAPIData, postAPIGLForm, refreshGLAPIData } from '../../../../api/invoicing';
import { getGLs } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { GL_API_SETTINGS } from '../../../../reusable-components/datagrid/invoiceColumns';
import Iconify from '../../../../reusable-components/iconify';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { BookingTypeSubmitRules } from '../../../../utils/globalEnums';

export default function GlAPIModal() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const lastStep = 2;
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState([]);
  const [value, setValue] = useState({ id: '', facility: '', mode: '' });

  const { invoicingGLs, editableGLFacilities } = useSelector((state) => state.invoicing.data);

  const existingFacilityCodes = useMemo(() =>
    invoicingGLs?.filter(v => v.facilityId === value?.facility?.value)
      ?.map((v) => ({ code: v.glCode ?? v.category, name: v.subcategory?.toLowerCase()?.trim() ?? v.categoryDesc?.toLowerCase()?.trim() }))
    , [invoicingGLs, value?.facility?.value]);

  const filteredData = useMemo(() => data.map(d => ({
    ...d, isNew: value.mode === '3' ?
      !existingFacilityCodes.some(c => c.code === d.categoryKey) :
      !existingFacilityCodes.some(c => c.code === d.code)
  }))
    , [data, existingFacilityCodes]);

  const isStepValid = useMemo(() => {
    switch (activeStep) {
      case 0:
        return !!value?.facility?.label;
      case 1:
        return value.mode?.length > 0;
      case 2:
        return rowSelectionModel.length > 0;
      default:
        return true;
    }
  }, [activeStep, value, rowSelectionModel]);

  const handleClickOpen = () => {
    setValue({
      id: '',
      facility: '',
      mode: '',
    });
    setOpen(true);
    setProgress(0);
  };

  const handleClose = () => {
    setActiveStep(0);
    setProgress(0);
    setRowSelectionModel([]);
    setValue({
      id: '',
      facility: '',
      mode: ''
    });
    setOpen(false);
  };

  const handleBack = () => {
    setProgress(0);
    if (value?.facility?.extraValue === BookingTypeSubmitRules.sendThroughQuickbooks) setActiveStep(0);
    else setActiveStep((prevActiveStep) => prevActiveStep > 0 ? prevActiveStep - 1 : prevActiveStep);
  };

  const handleNext = async () => {
    if (activeStep === lastStep) {
      await saveGLData();
      handleClose();
    } else if (activeStep === 0) {
      if (isStepValid) {
        if (value.facility.extraValue === BookingTypeSubmitRules.sendThroughQuickbooks) {
          await getGLData();
          setValue({ ...value, mode: '1' });
          setActiveStep((prevActiveStep) => prevActiveStep + 2);
        } else setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 1) {
      await getGLData();
      if (isStepValid) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const getGLData = async () => {
    await setIsLoading(true);
    const res = await getGLAPIData(value.facility.value, value.mode);

    if (res.status === 200) {
      if (res.data) setData(res.data);
      setIsLoading(false);
      setIsRefreshLoading(true);
    }
    if (res.status === 204) {
      setIsLoading(false);
      setIsRefreshLoading(true);
    }
  }

  const refreshAPIGLData = async () => {
    await refreshGLAPIData(value.facility.value, value.mode)
      .then((res) => {
        if (res.status === 200) {
          if (res.data) setData(res.data);
          setIsRefreshLoading(false);
          setProgress(0);
        }
        if (res.status === 204) {
          setIsRefreshLoading(false);
          setProgress(0);
        }
      }).catch(() => {
        setIsRefreshLoading(false);
        setProgress(0);
        enqueueSnackbar(`Integration connection failed - check permissions in intacct`, {
          variant: 'error',
        });
      });
  }

  useEffect(() => {
    if (isRefreshLoading) {
      refreshAPIGLData();
    }
  }, [isRefreshLoading])


  const saveGLData = async () => {
    const response = await postAPIGLForm(value, rowSelectionModel);
    if (response.data.error) {
      enqueueSnackbar(`Error: ${response.data.error}`, {
        variant: 'error',
      });
    } else if (response.status === 200) {
      enqueueSnackbar(`Successfully added ${response.data} GL${response.data === 1 ? '' : 's'}`, {
        variant: 'success',
      });
      dispatch(getGLs());
      setOpen(false);
    } else
      enqueueSnackbar(`Error: Could not add GLs`, {
        variant: 'error',
      });
  };

  useEffect(() => {
    setRowSelectionModel([]);
    setData([]);
  }, [value.facility]);


  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 85 ? prevProgress : prevProgress + 10));
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const columns = value.mode === '1' ? GL_API_SETTINGS.slice(2, 4) :
    value.mode === '3' ? GL_API_SETTINGS.slice(0, 2) :
      GL_API_SETTINGS;


  return (
    <ScopeGuard scopes={['Edit-Invoice-GLs']} allowAdmin>
      <Button
        variant="outlined"
        sx={{ mr: 1 }}
        size="small"
        color="secondary"
        onClick={handleClickOpen}
        startIcon={<ApiIcon />}
      >
        Import GLs
      </Button>

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth={activeStep === lastStep ? "xl" : "sm"} fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          {activeStep > 0 && <IconButton title="Back" onClick={handleBack}
            style={{
              position: 'absolute',
              left: 8,
              top: 8, color: 'gray'
            }}>
            <Iconify icon="ic:baseline-arrow-back" width={28} height={28} />
          </IconButton>}
          Import{value?.facility?.extraValue ? value.facility.extraValue === BookingTypeSubmitRules.sendThroughQuickbooks ? " Quickbooks" : " Intacct" : ""} GLs
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel color="secondary" width="600px">
            <Step key={"1"}>
              <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{"Choose Facility"}</StepLabel>
            </Step>
            <Step key={"2"}>
              <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{"Choose Mode"}</StepLabel>
            </Step>
            <Step key={"3"}>
              <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{"Add GLs"}</StepLabel>
            </Step>
          </Stepper>
          <Box>
            {activeStep === 0 &&
              <Stack spacing={3} justifyContent="center" sx={{ px: 8 }}>
                <Typography variant={"subtitle1"} sx={{ mb: 1 }}>
                  Facility
                </Typography>
                <Autocomplete
                  size="small"
                  value={value.facility}
                  isOptionEqualToValue={(option, value) => option?.id === value?.facility?.id}
                  options={editableGLFacilities || []}
                  onChange={(e, newValue) => setValue({ ...value, facility: newValue })}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
                  renderOption={(props, option) => {
                    const renderedOption = option.extraValue ?
                      (<Grid {...props} justifyContent="space-between">
                        <Grid item xs={10}>{`${option.label}`}
                        </Grid>
                        <Grid item xs={2} display="flex" alignItems="center">
                          <Iconify icon={option.extraValue === BookingTypeSubmitRules.sendThroughQuickbooks ? "simple-icons:quickbooks" : "simple-icons:sage"} />
                        </Grid>
                      </Grid>) : (
                        <Tooltip title={`${option.label} is not integrated with intacct`}>
                          <div>
                            <Box {...props} justifyContent="space-between" style={{ pointerEvents: 'none', opacity: "50%" }}>
                              <Grid item xs={10}>{`${option.label}`}</Grid>
                              <Grid item xs={2}>
                                <LinkOff />
                              </Grid>
                            </Box>
                          </div>
                        </Tooltip>);

                    return renderedOption;
                  }}
                  sx={{
                    width: "95%",
                    mt: 1,
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' }
                  }}
                />
              </Stack>
            }
            {activeStep === 1 &&
              <Grid container spacing={3} justifyContent="center" sx={{ px: 8, py: 4 }}>
                <ToggleButtonGroup color='info' fullWidth
                  size='large'
                  value={value.mode}
                  exclusive
                  onChange={(m) => setValue({ ...value, mode: m.target.value })}
                  sx={{ display: 'flex', gap: 1 }}
                >
                  <ToggleButton value='3' >
                    Departments Only
                  </ToggleButton>
                  <ToggleButton value='1' >
                    GLs Only
                  </ToggleButton>
                  <ToggleButton value='2' >
                    GLs & Categories
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            }
            {activeStep === lastStep && <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }} >
                <Typography variant="h5" sx={{ ml: 1, mb: 1, flex: 1 }}>
                  {value?.facility?.label}{' '}
                  {filteredData &&
                    <Typography variant="caption" display="inline" color="info.main">
                      {filteredData.filter(d => d.isNew).length} {value.mode === '3' ? ' New Departments ' : ' New GLs '} | {filteredData.filter(d => !d.isNew).length} {value.mode === '3' ? ' Updated Departments ' : ' Updated GLs '}
                    </Typography>}
                </Typography>

                {isRefreshLoading &&
                  <Typography variant="caption" sx={{ color: "warning.main", display: "flex", alignItems: "center" }}>
                    <CircularProgress variant="determinate" color="warning" size={18} sx={{ mr: 0.5 }} value={progress} /> Refreshing {progress}%
                  </Typography>
                }

                <Typography variant="h5" sx={{ ml: 1, mb: 1, flex: 1 }}>
                  {/*Placeholder to center text*/}
                </Typography>
              </Box>
              <Stack spacing={3} width={"100%"}>
                <CustomDataGrid
                  checkboxSelection
                  onRowSelectionModelChange={(newRowSelectionModel) => { if (!isRefreshLoading) setRowSelectionModel(newRowSelectionModel) }}
                  keepNonExistentRowsSelected
                  rowSelectionModel={rowSelectionModel}
                  gridId="inv-api-gls"
                  keepNon
                  tableBoxSX={{ height: '330px' }}
                  boxSX={{ height: '400px' }}
                  scrollbarHeight={'250px'}
                  data={filteredData || []}
                  gridColumns={[...columns]}
                  maxValue={{ ppdBudget: 2 }}
                  pinnedColumns={{ right: ['actions'] }}
                  sort={[{ field: 'name', sort: 'asc' }]}
                  isLoading={isLoading}
                />
              </Stack>
            </>}
          </Box>
        </DialogContent>
        <DialogActions>
          <AwaitButton
            variant='contained'
            color="primary"
            size="medium"
            disabled={!isStepValid || isRefreshLoading}
            sx={{ width: 150 }}
            onClick={handleNext}
          >
            {activeStep === lastStep ? `Save ${rowSelectionModel.length > 0 ? `(${rowSelectionModel.length})` : ''}` : 'Next'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </ScopeGuard >
  );
}